import { Decimal, decimal, percentOf } from "./money.js";
import { RANGE_WIDTHS, } from "./contracts.js";
/** Keep the original rational basis across quotes; round requested bounds outward. */
export function recenterRange(basis, currentPrice) {
    const r = decimal(basis.referencePrice), l = decimal(basis.lowerPrice), u = decimal(basis.upperPrice), c = decimal(currentPrice);
    manualRange(basis.lowerPrice, basis.upperPrice);
    if (r.units <= 0n || c.units <= 0n || l.compare(r) > 0 || u.compare(r) < 0)
        throw new Error("Original range must contain its reference price");
    const scale = Math.min(80, Math.max(40, l.scale, u.scale, c.scale));
    const scaled = (bound, roundUp) => {
        const product = bound.mul(c);
        const n = product.units * 10n ** BigInt(r.scale + scale);
        const d = r.units * 10n ** BigInt(product.scale);
        return new Decimal((n + (roundUp ? d - 1n : 0n)) / d, scale).toString();
    };
    const percent = (n, d) => n.mul(decimal("100")).div(d).toString();
    return {
        ...manualRange(scaled(l, false), scaled(u, true)),
        belowPercent: basis.inverted ? percent(u.sub(r), u) : percent(r.sub(l), r),
        abovePercent: basis.inverted ? percent(r.sub(l), l) : percent(u.sub(r), r),
    };
}
export function manualRange(lowerPrice, upperPrice) {
    const lo = decimal(lowerPrice), hi = decimal(upperPrice);
    if (lo.units <= 0n || lo.compare(hi) >= 0)
        throw new Error("Range requires 0 < min < max");
    return { lowerPrice: lo.toString(), upperPrice: hi.toString() };
}
export function calculateRange(input) {
    const { widthPercent: width, mode } = input;
    if (!RANGE_WIDTHS.includes(width) || !["down", "up", "balanced"].includes(mode))
        throw new Error("Invalid range preset");
    const p = decimal(input.referencePrice);
    if (p.units <= 0n)
        throw new Error("Reference price must be positive");
    const short = mode === "balanced" ? width / 2 : input.shortPercent;
    if (short === undefined ||
        (mode !== "balanced" && ![3, 5, 10, 15].includes(short)) ||
        short > width / 2)
        throw new Error("Choose valid short offset or symmetric/manual range");
    const below = mode === "down" ? width - short : short;
    const above = mode === "up" ? width - short : short;
    return {
        lowerPrice: percentOf(input.referencePrice, String(100 - below)),
        upperPrice: percentOf(input.referencePrice, String(100 + above)),
        lowerOffsetPercent: String(below),
        upperOffsetPercent: String(above),
        symmetric: below === above,
    };
}
/** Generic tick snapping; the SDK adapter must supply actual tick-to-price calculations. */
export function roundTicks(lowerTick, upperTick, spacing) {
    if (![lowerTick, upperTick, spacing].every(Number.isSafeInteger) ||
        spacing <= 0 ||
        lowerTick >= upperTick)
        throw new Error("Invalid ticks");
    return {
        lowerTick: Math.floor(lowerTick / spacing) * spacing,
        upperTick: Math.ceil(upperTick / spacing) * spacing,
    };
}
